import React, {useEffect, useMemo, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {Box, Button, Flex, HStack, useMultiStyleConfig, usePrevious} from '@chakra-ui/react'
import {LineIcon} from '../icons'
import {useIntl} from 'react-intl'
import useOcapiSite from '../../hooks/useOcapiSite'

const LinePagination = ({current, total, onClick, ...props}) => {
    const previous = usePrevious(current)
    const {wishlistEnabled} = useOcapiSite().preferences

    const items = useMemo(() => Array.from({length: total}, () => ({})), [total])
    const styles = useMultiStyleConfig('ImageGallery', props)
    const scrollPaginationRef = useRef()
    const [paginationLeft, setPaginationLeft] = useState(0)

    const {formatMessage} = useIntl()

    const paginationLineWidth = 32

    useEffect(() => {
        let direction = previous < current ? 1 : previous > current ? -1 : 0
        if (direction < 0 && current === total) {
            // scroll from first to last element
            scrollPaginationRef.current.scrollTo({
                top: 0,
                left: 355 * paginationLineWidth + 13,
                behavior: 'smooth'
            })
            setPaginationLeft((paginationLineWidth - 1) * total - 15 + 'px')
        } else if (direction > 0 && current === 0) {
            // scroll from last fo first element
            scrollPaginationRef.current.scrollTo({
                top: 0,
                left: 0,
                behavior: 'smooth'
            })
            setPaginationLeft('0px')
        } else {
            if (current > 5) {
                scrollPaginationRef.current.scrollTo({
                    top: 0,
                    left: (current - 5) * paginationLineWidth,
                    behavior: 'smooth'
                })
                if (current === items.length - 1) {
                    setPaginationLeft(paginationLineWidth * 6 + 5 + 'px')
                } else if (current === items.length - 2) {
                    setPaginationLeft(paginationLineWidth * 5 + 5 + 'px')
                } else {
                    setPaginationLeft(paginationLineWidth * 5 + 'px')
                }
            } else {
                scrollPaginationRef.current.scrollTo({
                    top: 0,
                    left: 0,
                    behavior: 'smooth'
                })
                setPaginationLeft(paginationLineWidth * current + 'px')
            }
        }
    }, [current, items.length, previous, total])

    return (
        <Flex
            alignItems="center"
            height={6}
            paddingRight={
                // wishlist toggle is absolutely positioned to the right, so
                // when it's enabled we need to add padding,
                // so it doesn't overlap with the wishlist toggle
                wishlistEnabled ? 10 : null
            }
            pointerEvents="auto"
        >
            <Box {...styles.controls}>
                <Box width="100%" position="absolute" top={0} left={0}>
                    <LineIcon
                        height="1px"
                        verticalAlign="top"
                        data-testid="pagination-line"
                        position="absolute"
                        transition="all .5s ease-in-out"
                        left={paginationLeft}
                    />
                </Box>
                <HStack
                    data-testid="emotional-gallery-pagination"
                    maxWidth="222px"
                    overflow="auto"
                    ref={scrollPaginationRef}
                    sx={{
                        '&::-webkit-scrollbar': {
                            display: 'none'
                        },
                        scrollbarWidth: 'none'
                    }}
                    spacing={1}
                    marginLeft={-1}
                >
                    {items?.map((_, index) => {
                        return (
                            <Button
                                variant="linePagination"
                                key={index}
                                onClick={() => onClick(index - current)}
                                aria-label={formatMessage(
                                    {
                                        id: 'image_gallery.button.assistive_msg.go_to_image',
                                        defaultMessage: 'Go to image {index}'
                                    },
                                    {index: index + 1}
                                )}
                                {...styles.paginationItem}
                                aria-current={index === current ? 'true' : 'false'}
                            >
                                <LineIcon height="1px" />
                            </Button>
                        )
                    })}
                </HStack>
            </Box>
        </Flex>
    )
}

LinePagination.propTypes = {
    current: PropTypes.number,
    total: PropTypes.number,
    direction: PropTypes.number,
    onClick: PropTypes.func
}

export default LinePagination
